import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  MousePointer,
  Package,
  Target,
  ClipboardList,
  TestTube,
  FlaskConical,
  MessageCircle,
  Settings,
  Car,
  Pill,
} from 'lucide-react';
import SimulationCard from '../../components/SimulationCard';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import { LockedFeature } from '../../components/subscription/UpgradePrompt';

export type UseCaseType =
  | 'survey-and-focus-groups'
  | 'concept-test'
  | 'driving-concept-test'
  | 'vitamin-concept-test'
  | 'packaging-review-v2'
  | 'claims-testing'
  | 'attribution'
  | 'channel-strategy'
  | 'image-survey'
  | 'healthcare-chat-v2'
  | 'hbs-chat'
  | 'test-use-case'
  | null;

interface UseCaseOption {
  label: string;
  type: UseCaseType;
  icon: React.ReactNode;
  description: string;
  redirect?: boolean;
}

interface CategoryConfig {
  title: string;
  useCases: UseCaseOption[];
}

interface LandingScreenProps {
  onSelectUseCase: (useCaseType: UseCaseType, useCaseTitle: string) => void;
}

const getCategories = (isAdmin: boolean): CategoryConfig[] => [
  {
    title: 'Research',
    useCases: [
      {
        label: 'Surveys (Qual and Quant)',
        type: 'survey-and-focus-groups',
        icon: <ClipboardList className="w-5 h-5" />,
        description: 'Run qualitative and quantitative interviews to get deep insights',
      },
      {
        label: 'Consumer Insights Chat',
        type: 'healthcare-chat-v2',
        icon: <MessageCircle className="w-5 h-5" />,
        description: 'Chat with AI-powered consumer digital twins',
        redirect: true,
      },
      ...(isAdmin ? [{
        label: 'Test Use Case',
        type: 'test-use-case' as UseCaseType,
        icon: <Settings className="w-5 h-5" />,
        description: 'Simulate a test to get insights',
      }] : []),
    ],
  },
  {
    title: 'Product & Innovation',
    useCases: [
      {
        label: 'Concept Test',
        type: 'concept-test',
        icon: <TestTube className="w-5 h-5" />,
        description: 'Test product concepts with AI-simulated consumers to predict purchase intent',
      },
      {
        label: 'Packaging Review',
        type: 'packaging-review-v2',
        icon: <Package className="w-5 h-5" />,
        description: 'Advanced packaging analysis with technical compliance, shelf test, and persona reactions',
      },
      {
        label: 'Claims Testing',
        type: 'claims-testing',
        icon: <FlaskConical className="w-5 h-5" />,
        description: 'Test and rank product claims using MaxDiff analysis to find what resonates most',
      },
      {
        label: 'Driving Concept Test',
        type: 'driving-concept-test',
        icon: <Car className="w-5 h-5" />,
        description: 'Test driving concepts with AI-simulated driver populations across D-score dimensions',
      },
      {
        label: 'Vitamin Concept Test',
        type: 'vitamin-concept-test',
        icon: <Pill className="w-5 h-5" />,
        description: 'Test CPG/vitamin product concepts with AI-simulated consumer twins',
      },
    ],
  },
  {
    title: 'Marketing',
    useCases: [
      {
        label: 'AB Test Messaging',
        type: 'attribution',
        icon: <Target className="w-5 h-5" />,
        description: 'Learn what drives or blocks conversions',
      },
      {
        label: 'A/B Test or Review Creatives and Ads',
        type: 'channel-strategy',
        icon: <TrendingUp className="w-5 h-5" />,
        description: 'Instantly predict which creative will perform best, get insights on what resonates',
      },
      {
        label: 'Creative Rank Test',
        type: 'image-survey',
        icon: <MousePointer className="w-5 h-5" />,
        description: 'Run a ranked click test to identify the best creatives for your listing',
      },
    ],
  },
];

const DRIVING_ALLOWED_DOMAINS = (import.meta.env.VITE_DRIVING_ALLOWED_DOMAINS || '')
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

const VITAMIN_ALLOWED_DOMAINS = (import.meta.env.VITE_VITAMIN_ALLOWED_DOMAINS || '')
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

const LandingScreen: React.FC<LandingScreenProps> = ({ onSelectUseCase }) => {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const { hasFeatureAccess, currentSubscription } = useSubscription();

  const categories = getCategories(isAdmin);

  const userDomain = user?.email?.split('@')[1]?.toLowerCase() || '';

  const isDrivingAllowed = (() => {
    if (isAdmin) return true;
    if (currentSubscription?.id === 'internal') return true;
    return !!userDomain && DRIVING_ALLOWED_DOMAINS.includes(userDomain);
  })();

  const isVitaminAllowed = (() => {
    if (isAdmin) return true;
    if (currentSubscription?.id === 'internal') return true;
    return !!userDomain && VITAMIN_ALLOWED_DOMAINS.includes(userDomain);
  })();

  const handleUseCaseClick = (useCase: UseCaseOption) => {
    if (useCase.redirect) {
      if (useCase.type === 'healthcare-chat-v2') {
        navigate('/ask-natural-organic');
        return;
      }
      if (useCase.type === 'hbs-chat') {
        window.open('/ask-natural-organic', '_blank');
        return;
      }
    }

    onSelectUseCase(useCase.type, useCase.label);
  };

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Start a new study.
        </h1>
        <p className="text-gray-500">
          Run research, test ideas, and evaluate marketing with your AI audience.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {categories.map((category) => (
          <div key={category.title}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {category.title}
            </h3>
            <div className="flex flex-col gap-4">
              {category.useCases.map((useCase) => {
                if (useCase.type === 'driving-concept-test' && !isDrivingAllowed) return null;
                if (useCase.type === 'vitamin-concept-test' && !isVitaminAllowed) return null;

                const isChatRedirect = useCase.redirect;
                const hasAccess = isAdmin || isChatRedirect || hasFeatureAccess(useCase.type as string);
                const isLocked = !hasAccess;

                if (isLocked) {
                  return (
                    <LockedFeature
                      key={useCase.label}
                      feature={useCase.label}
                      requiredPlan={currentSubscription?.id === 'free' ? 'Starter' : 'Higher tier'}
                    >
                      <SimulationCard
                        icon={useCase.icon}
                        title={useCase.label}
                        description={useCase.description}
                        disabled
                        onClick={() => {}}
                      />
                    </LockedFeature>
                  );
                }

                return (
                  <SimulationCard
                    key={useCase.label}
                    icon={useCase.icon}
                    title={useCase.label}
                    description={useCase.description}
                    onClick={() => handleUseCaseClick(useCase)}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LandingScreen;
